import { Card, Button, Badge } from "@/design-system/components/ui";
import { ShieldCheck } from "lucide-react";
import type { InvestorMaskedDto } from "@/types/investors";
import { formatDate } from "../../shared/format";

interface CertCardSpec {
  title: string;
  verifiedAt: string | null;
  /** Investor-DTO-backed cards expose a verified date; others defer. */
  detail: string;
}

export function AccreditationKycTab({
  investor,
  onOpenCertifications,
}: {
  investor: InvestorMaskedDto;
  onOpenCertifications: () => void;
}) {
  const cards: CertCardSpec[] = [
    {
      title: "Accreditation Verified",
      verifiedAt: investor.accreditationVerifiedAt,
      detail: `Basis: ${investor.accreditationBasis}`,
    },
    {
      title: "AML / KYC Verified",
      verifiedAt: investor.amlKycVerifiedAt,
      detail: "Anti-money-laundering and know-your-customer review.",
    },
    {
      title: "Banking Instructions Verified",
      verifiedAt: investor.hasBankingInstructions ? null : null,
      detail: investor.hasBankingInstructions
        ? "Banking instructions on file — dual-control verification tracked on the Certifications tab."
        : "No banking instructions on file yet.",
    },
  ];

  return (
    <div className="flex flex-col gap-space-4">
      <p className="text-small text-gray-mid m-0">
        Operator-friendly view of the three investor-side certifications. The
        Certifications tab carries the full auditor history.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-space-4">
        {cards.map((c) => (
          <Card key={c.title}>
            <div className="flex items-center gap-space-2 mb-space-2">
              <ShieldCheck
                size={16}
                className={c.verifiedAt ? "text-status-success" : "text-gray-mid"}
                aria-hidden
              />
              <h3 className="font-display font-bold text-small m-0">{c.title}</h3>
            </div>
            <div className="mb-space-2">
              {c.verifiedAt ? (
                <Badge status="finalized">Verified</Badge>
              ) : (
                <Badge status="review">Not verified</Badge>
              )}
            </div>
            <dl className="m-0 text-caption text-gray-mid">
              <div className="flex flex-col mb-space-1">
                <dt className="uppercase tracking-label">Verified at</dt>
                <dd className="m-0 text-onyx">{formatDate(c.verifiedAt)}</dd>
              </div>
              <p className="m-0 mt-space-1">{c.detail}</p>
            </dl>
            <Button
              variant="secondary"
              size="sm"
              className="mt-space-3"
              onClick={onOpenCertifications}
            >
              {c.verifiedAt ? "Re-verify" : "Verify"}
            </Button>
          </Card>
        ))}
      </div>
    </div>
  );
}
